<template>
  <div
    v-if="hasPermission(userPermissions['ADD_RESPONSIBLE'])"
    class="modal-card"
  >
    <b-loading :isFullPage="false" :active="isLoading" />
    <header class="modal-card-head">
      <p class="modal-card-title is-size-5 has-text-centered">
        {{ General.ASSIGN_RESPONSIBLE }}
      </p>
      <div class="close-button cursor-pointer" @click.stop="close">
        <b-icon icon="close" />
      </div>
    </header>
    <div class="modal-content-container">
      <section class="spacing-p-10">
        <div class="spacing-mb-10">
          <div class="has-text-color-7 has-text-weight-bold">
            {{ General.UNITY }}
          </div>
          <div class="has-text-color-7">
            <span>{{ user.capacity.part_abbreviation }}/</span>
            <span>{{ user.capacity.capacity_abbreviation }}</span>
          </div>
        </div>
        <b-field customClass="has-text-color-7" :label="General.RESPONSIBLE">
          <b-autocomplete
            ref="responsibleInput"
            v-model="name"
            class="overflow-list"
            :placeholder="General.PLACEHOLDER.TYPE_SEARCH_FULL_USER_UNIT"
            :loading="isFetching"
            :openOnFocus="openOnFocus"
            :data="getUsersFromSearchFormat"
            field="displayName"
            :clearable="clearable"
            :maxHeight="100"
            expanded
            keepFirst
            @typing="filteredUsersData"
            @select="option => (selectedUser = option)"
          >
            <template slot="empty">
              <p v-if="!isFetching">
                {{ General.MESSAGES.NO_RECORDS_FOUND.toUpperCase() }}
              </p>
              <p v-else>
                {{ General.MESSAGES.LOADING_RECORDS.toUpperCase() }}
              </p>
            </template>
          </b-autocomplete>
        </b-field>
      </section>
      <footer class="modal-card-foot is-justify-content-flex-end">
        <div class="buttons">
          <b-tooltip
            :label="General.TEXTS.CLICK_ASSIGN_SELF"
            :type="isContrast ? 'is-white' : 'is-blue-oil'"
            :size="isMobile ? 'is-small' : 'is-medium'"
            class="mr-5"
            multilined
          >
            <b-button
              :type="isContrast ? 'is-white-dark' : 'is-primary'"
              :disabled="selectedUser !== null"
              outlined
              @click="checkDispathFilled(true)"
            >
              {{ General.ASSIGN_SELF.toUpperCase() }}
            </b-button>
          </b-tooltip>
          <b-button
            :type="isContrast ? 'is-white-dark' : 'is-primary'"
            :disabled="selectedUser === null"
            @click="checkDispathFilled()"
          >
            {{ General.ASSIGN.toUpperCase() }}
          </b-button>
          <b-button
            :type="isContrast ? 'is-white-dark' : 'is-primary'"
            outlined
            @click="close"
          >
            {{ General.CANCEL.toUpperCase() }}
          </b-button>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import { mapGetters, mapActions } from "vuex";
import { App, General } from "../../utils/constants";
import { USER_PERMISSIONS as userPermissions } from "@/utils/enumerate";
import ModalConfirmResponsible from "@/views/App/ModalConfirmResponsible.vue";
import ModalWarning from "@/views/App/ModalWarning.vue";

export default {
  components: {},

  props: {
    processList: {
      type: Array,
      default: () => []
    },
    clearSelection: {
      type: Function,
      default: () => null
    },
    onClose: {
      type: Function,
      default: () => null
    }
  },

  data() {
    return {
      App,
      General,
      isLoading: false,
      selectedUser: null,
      isFetching: false,
      openOnFocus: false,
      name: "",
      responsible: null,
      clearable: false,
      userPermissions
    };
  },

  computed: {
    ...mapGetters({
      user: "getCurrentUser",
      getUsersFromSearch: "getUsersFromSearch",
      hasPermission: "hasPermission",
      getCapacityById: "getCapacityById",
      isContrast: "isContrast",
      isMobile: "isMobile"
    }),

    getUsersFromSearchFormat() {
      const formattedUsers = this.getUsersFromSearch.map(user => ({
        ...user,
        displayName: `${user.name}${
          user.capacity_selected
            ? " - " + user.capacity_selected.abbreviation
            : ""
        }`
      }));

      if (!this.isBatch) {
        const processCapacity =
          this.processList[0].capacity_object ||
          this.processList[0].capacity ||
          this.processList[0].origin;
        return formattedUsers.filter(
          user =>
            !(
              user.id == this.processList[0].user_responsible_id &&
              user.capacity_selected?.id == processCapacity.id
            )
        );
      }
      return formattedUsers;
    },

    isBatch() {
      return this.processList.length > 1;
    }
  },

  mounted() {
    this.$refs.responsibleInput?.focus();
  },

  methods: {
    ...mapActions({
      userResponsibleRequest: "userResponsibleRequest",
      userFilter: "userGenericFilterRequest",
      hasAutoDraftWritten: "hasAutoDraftWritten"
    }),

    close() {
      this.clearSelection();
      this.$parent.close();
    },

    getSelectedResponsible(self) {
      return self
        ? {
            id: this.user.id,
            capacity_selected: this.getCapacityById(
              this.user.capacity.capacity_id
            ),
            name: this.user.name
          }
        : this.selectedUser;
    },

    async checkDispathFilled(self = false) {
      const selectedResponsible = this.getSelectedResponsible(self);

      const results = await Promise.all(
        this.processList.map(actual_process =>
          this.hasAutoDraftWritten({
            processId: actual_process.id,
            capacityId: selectedResponsible.capacity_selected.id
          })
        )
      );
      const showModal = results.find(result => result.is_coordination_to_cell);
      if (showModal)
        this.$buefy.modal.open({
          parent: this,
          component: ModalWarning,
          props: {
            title: `${this.General.WARNING}!`,
            message: `${this.General.MESSAGES.DISPATCH_FILLED}?`,
            confirm: () => this.confirmAction(selectedResponsible),
            buttons: [
              { label: General.YES, isConfirm: false, action: true },
              { label: General.NO, isConfirm: true, action: false }
            ]
          },
          hasModalCard: true,
          trapFocus: true
        });
      else this.confirmAction(selectedResponsible);
    },

    async confirmAction(selectedResponsible) {
      const differentResponsibleProcesses = this.processList.filter(
        process =>
          Boolean(process.user_responsible_id) &&
          process.user_responsible_id !== selectedResponsible.id
      );

      const userResponsible = this.processList.map(process => {
        return {
          process_id: process.id,
          user_responsible_id: selectedResponsible.id,
          capacity_selected: selectedResponsible.capacity_selected
        };
      });

      const responsibleName =
        selectedResponsible.name +
        (selectedResponsible.capacity_selected
          ? " - " + selectedResponsible.capacity_selected.abbreviation
          : "");

      if (differentResponsibleProcesses.length) {
        this.$buefy.modal.open({
          parent: this,
          component: ModalConfirmResponsible,
          props: {
            isBatch: this.processList.length > 1,
            processList: differentResponsibleProcesses,
            onApply: async () => {
              await this.userResponsibleRequest({
                userResponsible,
                responsibleName
              });
              this.onClose();
            }
          },
          hasModalCard: true,
          trapFocus: true
        });
      } else {
        this.isLoading = true;
        await this.userResponsibleRequest({ userResponsible, responsibleName });
        this.isLoading = false;
        this.onClose();
      }
      this.close();
    },

    filteredUsersData: debounce(async function(name) {
      if (!name.length) {
        this.$store.commit("setUsersFromSearch", []);
        return;
      }

      this.isFetching = true;
      await this.userFilter({
        part_id: this.user.capacity.part_id,
        capacity: this.user.capacity.capacity_abbreviation,
        responsible: true,
        name
      });
      this.isFetching = false;
    }, 500)
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";

.modal-card {
  background-color: $color-1;
  display: flex;
  min-height: 320px;
  max-height: 75vh;
}

.modal-content-container {
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: space-between;
  overflow: auto;
  padding: 0 10px;
}

.card-body {
  display: flex;
  align-content: flex-start;
  flex-direction: column;
  flex: 1;
}

.custom-self-align-end {
  align-self: flex-end;
}
</style>
